import axios from 'axios';
import { supabase } from '../supabaseClient';
import { apiEndpoints, API_BASE_URL } from '../config/api';

export interface AccountIntegration {
  id: string;
  integration_type: 'gmail' | 'outlook' | 'imap';
  email_address: string;
  is_active: boolean;
  connected_at: string;
  last_sync_at?: string;
  sync_frequency_minutes: number;
}

export interface IntegrationError {
  error: string;
}

export async function getAccountIntegrations(accountId: string): Promise<{ integrations: AccountIntegration[] | null; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return { integrations: null, error: 'Not authenticated' };
    }

    const response = await axios.get(apiEndpoints.accountIntegrations(accountId, user.id));

    return { integrations: response.data.integrations };
  } catch (error: any) {
    return { integrations: null, error: error.response?.data?.error || 'Failed to fetch integrations' };
  }
}

export async function connectGmailIntegration(
  accountId: string,
  impersonationData?: {
    isImpersonating: boolean;
    impersonatedUserId: string;
  }
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    if (impersonationData?.isImpersonating) {
      // Use impersonation Gmail auth endpoint
      const authUrl = `${API_BASE_URL}/admin/impersonate/${impersonationData.impersonatedUserId}/account/${accountId}/gmailauth?currentUserId=${user.id}`;
      window.location.href = authUrl;
    } else {
      // Normal Gmail OAuth flow
      const authUrl = apiEndpoints.gmailAuth(user.id, accountId);
      window.location.href = authUrl;
    }

    return { success: true };
  } catch (error: any) {
    return { success: false, error: 'Failed to initiate Gmail connection' };
  }
}

export async function disconnectIntegration(
  accountId: string,
  integrationId: string,
  impersonationData?: {
    isImpersonating: boolean;
    impersonatedUserId: string;
  }
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    if (impersonationData?.isImpersonating) {
      // Use impersonation endpoint
      const response = await axios.delete(`${API_BASE_URL}/admin/impersonate/${impersonationData.impersonatedUserId}/account/${accountId}/integrations/${integrationId}`, {
        data: { currentUserId: user.id }
      });
      return { success: response.data.success || true };
    } else {
      // Normal disconnect
      const response = await axios.delete(apiEndpoints.disconnectIntegration(accountId, integrationId), {
        data: { userId: user.id }
      });
      return { success: response.data.success || true };
    }
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || 'Failed to disconnect integration' };
  }
}

export async function cleanupInvalidIntegrations(
  accountId: string,
  impersonationData?: {
    isImpersonating: boolean;
    impersonatedUserId: string;
  }
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

    if (impersonationData?.isImpersonating) {
      // Use impersonation endpoint
      const response = await axios.post(`${API_BASE_URL}/admin/impersonate/${impersonationData.impersonatedUserId}/account/${accountId}/integrations/cleanup`, {
        currentUserId: user.id
      });
      return { success: response.data.success || true };
    } else {
      // Normal cleanup
      const response = await axios.post(`${API_BASE_URL}/accounts/${accountId}/integrations/cleanup`, {
        userId: user.id
      });
      return { success: response.data.success || true };
    }
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || 'Failed to cleanup integrations' };
  }
}

export interface GmailConflictCheck {
  hasConflict: boolean;
  conflictingAccounts: Array<{
    accountId: string;
    accountName: string;
  }>;
  canConnect: boolean;
}

export async function checkGmailConflict(
  accountId: string,
  emailAddress: string,
  impersonationData?: {
    isImpersonating: boolean;
    impersonatedUserId: string;
  }
): Promise<{ data: GmailConflictCheck | null; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return { data: null, error: 'Not authenticated' };
    }

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

    if (impersonationData?.isImpersonating) {
      // Use impersonation endpoint
      const response = await axios.post(`${API_BASE_URL}/admin/impersonate/${impersonationData.impersonatedUserId}/account/${accountId}/integrations/check-gmail`, {
        currentUserId: user.id,
        emailAddress: emailAddress
      });
      return { data: response.data };
    } else {
      // Normal conflict check
      const response = await axios.post(`${API_BASE_URL}/accounts/${accountId}/integrations/check-gmail`, {
        userId: user.id,
        emailAddress: emailAddress
      });
      return { data: response.data };
    }
  } catch (error: any) {
    return { data: null, error: error.response?.data?.error || 'Failed to check Gmail conflict' };
  }
} 